import { flattenCategoriesV2 } from "../services/CategoriesService";
import type {
  CategoryV2,
  HomeDepotCategory,
} from "../services/CategoriesService";
import type { StoreProductResult } from "../services/ProductSearchService";
import type { HomeDepotStoreProductResult } from "../services/ProductSearchService";
import type { ProductListItem, IbottaOffer } from "../types/Product";
import type { StoreData } from "../hooks/useStores";
import type {
  DollarGeneralStore,
  HomeDepotStore,
  WalmartStore,
} from "../utils/storeUtils";
import { formatHomeDepotStoreId, toLatLon } from "../utils/storeUtils";
import {
  buildRetailerLookupUrl,
  getRetailerProductUrl,
} from "../utils/productUrls";

export { buildRetailerLookupUrl };

export type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

type RelativeTimeOptions = {
  now?: number;
  emptyLabel?: string;
  t?: TranslateFn;
};

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const getProductUrl = getRetailerProductUrl;

export const formatPrice = (price: number) => {
  return USD_FORMATTER.format(price);
};

// `t` is optional so plain (non-React) callers keep the English fallback.
export const formatRelativeTime = (timestamp: number, now = Date.now(), t?: TranslateFn) => {
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return t ? t("products.daysAgo", { days: diffDays }) : `${diffDays}d ago`;
  if (diffHours > 0) return t ? t("products.hoursAgo", { hours: diffHours }) : `${diffHours}h ago`;
  if (diffMinutes > 0) return t ? t("products.minutesAgo", { minutes: diffMinutes }) : `${diffMinutes}m ago`;
  return t ? t("products.justNow") : "Just now";
};

export const formatLastUpdated = (
  timestamps: number[],
  options: RelativeTimeOptions = {}
) => {
  if (!timestamps || timestamps.length === 0) {
    return options.emptyLabel || (options.t ? options.t("products.noUpdates") : "No updates");
  }
  const latest = Math.max(...timestamps);
  return formatRelativeTime(latest, options.now, options.t);
};

export const formatFirstAppeared = (
  timestamp: number,
  options: RelativeTimeOptions = {}
) => {
  if (!timestamp) return options.emptyLabel || (options.t ? options.t("card.unknown") : "Unknown");
  return formatRelativeTime(timestamp, options.now, options.t);
};

export const isValidUpc = (upc: string | null | undefined): boolean => {
  return !!upc && upc !== "0" && upc !== "";
};

/** Ignore empty values and the Dynamo placeholder "0". */
export const normalizeProductCode = (code?: string | null): string | undefined => {
  const trimmed = code?.trim();
  if (!trimmed || trimmed === "0") return undefined;
  return trimmed;
};

/** True when the value is long enough to be a scannable UPC/EAN (not a Walmart item id). */
export const isScannableBarcode = (code: string | null | undefined): boolean => {
  const normalized = normalizeProductCode(code);
  if (!normalized) return false;
  return normalized.replace(/\D/g, "").length >= 12;
};

/**
 * Walmart store products: `upc` is the retailer item id (partition key), `sku` is the UPC when known.
 */
export const resolveWalmartSkuUpc = (
  upc?: string | null,
  sku?: string | null
): { itemSku?: string; barcode?: string } => {
  const itemSku = normalizeProductCode(upc);
  const storedBarcode = normalizeProductCode(sku);
  if (!itemSku && !storedBarcode) return {};

  if (storedBarcode && itemSku && storedBarcode !== itemSku) {
    return { itemSku, barcode: storedBarcode };
  }
  if (storedBarcode && isScannableBarcode(storedBarcode)) {
    return { itemSku, barcode: storedBarcode };
  }
  if (itemSku) return { itemSku };
  if (storedBarcode) return { barcode: storedBarcode };
  return {};
};

export const getWalmartBarcodeFromProduct = (
  upc?: string | null,
  sku?: string | null
): string | undefined => resolveWalmartSkuUpc(upc, sku).barcode;

const barcodeCacheKey = (upc: string, storeId: string) =>
  `dealhawk-barcode:${upc}:${storeId}`;

export const readCachedProductBarcode = (
  upc: string,
  storeId: string
): string | undefined => {
  try {
    const cached = sessionStorage.getItem(barcodeCacheKey(upc, storeId))?.trim();
    return cached || undefined;
  } catch {
    return undefined;
  }
};

export const writeCachedProductBarcode = (
  upc: string,
  storeId: string,
  barcode: string
) => {
  try {
    sessionStorage.setItem(barcodeCacheKey(upc, storeId), barcode.trim());
  } catch {
    /* ignore quota / private mode */
  }
};

export const getProductId = (product: {
  upc?: string | null;
  sku?: string | null;
}) => {
  return product.upc || product.sku || "";
};

export const getAllProductsWithSameUpc = (
  products: ProductListItem[],
  productsByUPC: Map<string, ProductListItem[]> | undefined,
  upc: string
) => {
  if (productsByUPC) {
    return productsByUPC.get(upc) || [];
  }
  return products.filter((p) => p.upc === upc);
};

export const getStoreCoordinates = (input: {
  storeId: string;
  retailer: string;
  loadedStores?: StoreData;
}): [number, number] | undefined => {
  const { storeId, retailer, loadedStores } = input;
  if (!loadedStores || !storeId) return undefined;

  if (retailer === "home_depot") {
    const stores = loadedStores as HomeDepotStore[];
    const formattedId = formatHomeDepotStoreId(storeId);
    const store = stores.find(
      (s) => formatHomeDepotStoreId(s.store_number) === formattedId
    );
    if (store?.latitude && store?.longitude) {
      return [parseFloat(store.latitude), parseFloat(store.longitude)];
    }
  } else if (retailer === "dollar_general") {
    const stores = loadedStores as DollarGeneralStore[];
    const store = stores.find((s) => s.storeNumber === storeId);
    if (store?.latitude && store?.longitude) {
      const coords = toLatLon(Number(store.longitude), Number(store.latitude));
      return [coords.lat, coords.lon];
    }
  } else {
    const stores = loadedStores as WalmartStore[];
    const store = stores.find((s) => s.store_id === storeId);
    if (store?.geo?.lat && store?.geo?.long) {
      return [store.geo.lat, store.geo.long];
    }
  }
  return undefined;
};

export const getCategoryName = (input: {
  categoryId: string;
  retailer: string;
  categories: CategoryV2[];
  homeDepotCategories?: HomeDepotCategory[];
}) => {
  const { categoryId, retailer, categories, homeDepotCategories } = input;
  if (!categoryId) return "";

  if (retailer === "home_depot" && homeDepotCategories) {
    for (const rootCategory of homeDepotCategories) {
      if (rootCategory.subcategories) {
        const subcategory = rootCategory.subcategories.find(
          (sub) => sub.id === categoryId
        );
        if (subcategory) {
          return subcategory.name;
        }
      }
    }

    const directMatch = homeDepotCategories.find(
      (cat) => cat.rootKey === categoryId
    );
    if (directMatch) {
      return directMatch.rootKey;
    }
  }

  if (retailer !== "home_depot" && categories) {
    try {
      const flattenedCategories = flattenCategoriesV2(categories);
      const foundCategory = flattenedCategories.find(
        (cat) => cat.categoryId === categoryId
      );
      if (foundCategory) {
        return foundCategory.name;
      }
    } catch {
      // ignore flatten errors
    }

    for (const rootCategory of categories) {
      if (rootCategory.subcategories) {
        const subcategory = rootCategory.subcategories.find(
          (sub) => sub.id === categoryId
        );
        if (subcategory) {
          return subcategory.name;
        }
      }
    }

    const directMatch = categories.find((cat) => cat.rootKey === categoryId);
    if (directMatch) {
      return directMatch.rootKey;
    }
  }

  return "";
};

export const getIsNewProduct = (firstInserted?: number, now = Date.now()) => {
  if (!firstInserted) return false;
  const diffMs = now - firstInserted;
  const diffHours = diffMs / (1000 * 60 * 60);
  return diffHours <= 24;
};

const hasSeriesChangeInLast24Hours = (
  values: number[] | undefined,
  lastUpdates: number[] | undefined,
  now = Date.now()
) => {
  if (!values || !lastUpdates) return false;
  const historyLength = Math.min(values.length, lastUpdates.length);
  if (historyLength < 2) return false;
  const cutoff = now - 24 * 60 * 60 * 1000;
  for (let i = historyLength - 1; i > 0; i -= 1) {
    if (lastUpdates[i] < cutoff) break;
    if (values[i] !== values[i - 1]) return true;
  }
  return false;
};

const lastSeriesChangeCache = new WeakMap<
  number[],
  WeakMap<number[], { length: number; timestamp: number | undefined }>
>();

const getCachedLastSeriesChangeTimestamp = (
  values: number[],
  lastUpdates: number[],
  historyLength: number
) => {
  let updatesCache = lastSeriesChangeCache.get(values);
  if (!updatesCache) {
    updatesCache = new WeakMap();
    lastSeriesChangeCache.set(values, updatesCache);
  }

  const cached = updatesCache.get(lastUpdates);
  if (cached?.length === historyLength) {
    return cached.timestamp;
  }

  let timestamp: number | undefined;
  for (let i = historyLength - 1; i > 0; i -= 1) {
    if (values[i] !== values[i - 1]) {
      timestamp = lastUpdates[i];
      break;
    }
  }

  updatesCache.set(lastUpdates, { length: historyLength, timestamp });
  return timestamp;
};

const getLastSeriesChangeTimestamp = (
  values: number[] | undefined,
  lastUpdates: number[] | undefined,
  minTimestamp?: number
) => {
  if (!values || !lastUpdates) return undefined;
  const historyLength = Math.min(values.length, lastUpdates.length);
  if (historyLength < 2) return undefined;
  if (minTimestamp === undefined) {
    return getCachedLastSeriesChangeTimestamp(values, lastUpdates, historyLength);
  }

  for (let i = historyLength - 1; i > 0; i -= 1) {
    const updateTimestamp = lastUpdates[i];
    if (minTimestamp !== undefined && updateTimestamp < minTimestamp) break;
    if (values[i] !== values[i - 1]) {
      return updateTimestamp;
    }
  }
  return undefined;
};

const getLastSeriesChangeTimestampInLast24Hours = (
  values: number[] | undefined,
  lastUpdates: number[] | undefined,
  now = Date.now()
) => {
  const cutoff = now - 24 * 60 * 60 * 1000;
  return getLastSeriesChangeTimestamp(values, lastUpdates, cutoff);
};

export const hasPriceChangeInLast24Hours = (
  priceHistory: number[] | undefined,
  lastUpdates: number[] | undefined,
  now = Date.now()
) => {
  return hasSeriesChangeInLast24Hours(priceHistory, lastUpdates, now);
};

export const hasPriceOrInventoryChangeInLast24Hours = (input: {
  priceHistory?: number[];
  backroomHistory?: number[];
  salesFloorHistory?: number[];
  lastUpdates?: number[];
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  return (
    hasSeriesChangeInLast24Hours(input.priceHistory, input.lastUpdates, now) ||
    hasSeriesChangeInLast24Hours(input.backroomHistory, input.lastUpdates, now) ||
    hasSeriesChangeInLast24Hours(input.salesFloorHistory, input.lastUpdates, now)
  );
};

export const getLastChangeTimestampInLast24Hours = (input: {
  priceHistory?: number[];
  backroomHistory?: number[];
  salesFloorHistory?: number[];
  lastUpdates?: number[];
  now?: number;
}) => {
  const now = input.now ?? Date.now();
  const priceChangeTimestamp = getLastSeriesChangeTimestampInLast24Hours(
    input.priceHistory,
    input.lastUpdates,
    now
  );
  const backroomChangeTimestamp = getLastSeriesChangeTimestampInLast24Hours(
    input.backroomHistory,
    input.lastUpdates,
    now
  );
  const salesFloorChangeTimestamp = getLastSeriesChangeTimestampInLast24Hours(
    input.salesFloorHistory,
    input.lastUpdates,
    now
  );
  return [priceChangeTimestamp, backroomChangeTimestamp, salesFloorChangeTimestamp].reduce<
    number | undefined
  >((latest, value) => {
    if (value === undefined) return latest;
    if (latest === undefined) return value;
    return Math.max(latest, value);
  }, undefined);
};

export const getLastChangeTimestamp = (input: {
  priceHistory?: number[];
  backroomHistory?: number[];
  salesFloorHistory?: number[];
  lastUpdates?: number[];
}) => {
  const priceChangeTimestamp = getLastSeriesChangeTimestamp(input.priceHistory, input.lastUpdates);
  const backroomChangeTimestamp = getLastSeriesChangeTimestamp(
    input.backroomHistory,
    input.lastUpdates
  );
  const salesFloorChangeTimestamp = getLastSeriesChangeTimestamp(
    input.salesFloorHistory,
    input.lastUpdates
  );
  return [priceChangeTimestamp, backroomChangeTimestamp, salesFloorChangeTimestamp].reduce<
    number | undefined
  >((latest, value) => {
    if (value === undefined) return latest;
    if (latest === undefined) return value;
    return Math.max(latest, value);
  }, undefined);
};

export const getLastSeenTimestamp = (
  lastUpdates: number[] | undefined,
  firstInserted = 0
) => {
  return Math.max(firstInserted || 0, ...(lastUpdates || []));
};

export const getLastUpdatedTimestamp = (input: {
  priceHistory?: number[];
  backroomHistory?: number[];
  salesFloorHistory?: number[];
  lastUpdates?: number[];
  firstInserted?: number;
  lastUpdated?: number;
}) => {
  // `lastUpdates` is change history, while `lastUpdated` is refreshed on every
  // successful observation. Include both so legacy rows without the scalar are
  // still correct, but never let an older history entry hide a newer refresh.
  return Math.max(
    getLastSeenTimestamp(input.lastUpdates, input.firstInserted),
    input.lastUpdated || 0
  );
};

export const getDisplayedLastChangeTimestamp = (
  lastChangeTimestamp: number | undefined,
  lastUpdatedTimestamp: number
) => lastChangeTimestamp || lastUpdatedTimestamp || 0;

export const getLatestValue = (values: number[] | undefined, fallback = 0) => {
  return values && values.length > 0 ? values[values.length - 1] : fallback;
};

export const getPriceChange = (prices: number[] | undefined) => {
  if (!prices || prices.length < 2) return null;
  const previousPrice = prices[prices.length - 2];
  const currentPrice = prices[prices.length - 1];
  if (currentPrice < previousPrice) return "down";
  if (currentPrice > previousPrice) return "up";
  return null;
};

export const getStockChange = (stocks: number[] | undefined) => {
  if (!stocks || stocks.length < 2) return null;
  const previousStock = stocks[stocks.length - 2];
  const currentStock = stocks[stocks.length - 1];
  if (currentStock > previousStock) return "up";
  if (currentStock < previousStock) return "down";
  return null;
};

// --- Ibotta -----------------------------------------------------------------
// Ibotta matches are a server-side brand + name match against offers captured
// from the Ibotta web app (never a barcode), so the UI frames them as
// "possible" and keeps the capture date and expiration visible.

const IBOTTA_DAY_MS = 24 * 60 * 60 * 1000;

export type IbottaExpirationStatus = "unknown" | "active" | "expiring_soon" | "expired";

export const getIbottaOffers = (product?: { ibottaOffers?: IbottaOffer[] | null } | null): IbottaOffer[] => {
  const offers = product?.ibottaOffers;
  if (!Array.isArray(offers)) return [];
  return offers.filter((offer) => offer && typeof offer.name === "string" && offer.name.trim().length > 0);
};

export const getIbottaBestAmount = (offers: IbottaOffer[]) =>
  offers.reduce((best, offer) => {
    const amount = Number(offer.amount);
    return Number.isFinite(amount) && amount > best ? amount : best;
  }, 0);

export const parseIbottaDate = (value?: string | number | null) => {
  if (value === undefined || value === null || value === "") return null;
  const date = typeof value === "number" ? new Date(value) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getIbottaExpirationStatus = (offer: IbottaOffer, now = Date.now()): IbottaExpirationStatus => {
  const expiration = parseIbottaDate(offer.expiration);
  if (!expiration) return offer.expired ? "expired" : "unknown";
  const remaining = expiration.getTime() - now;
  if (offer.expired || remaining <= 0) return "expired";
  if (remaining <= 2 * IBOTTA_DAY_MS) return "expiring_soon";
  return "active";
};

export const formatIbottaDate = (value?: string | number | null, locale?: string) => {
  const date = parseIbottaDate(value);
  return date ? date.toLocaleDateString(locale, { month: "short", day: "numeric" }) : "";
};

export const getCashbackAmount = (product?: { cashbackAmount?: number } | null) => {
  const amount = Number(product?.cashbackAmount);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

// Units the shopper must buy before the Walmart Cash reward is earned (1 = per item).
export const getCashbackMinQty = (product?: { cashbackAmount?: number; cashbackMinQty?: number } | null) => {
  if (getCashbackAmount(product) <= 0) return 1;
  const qty = Number(product?.cashbackMinQty);
  return Number.isFinite(qty) && qty > 1 ? Math.floor(qty) : 1;
};

// Reward spread across the units required to earn it.
export const getCashbackPerUnit = (product?: { cashbackAmount?: number; cashbackMinQty?: number } | null) => {
  const amount = getCashbackAmount(product);
  return amount > 0 ? amount / getCashbackMinQty(product) : 0;
};

export const getOfferType = (product?: { offerType?: string } | null) => {
  const value = String(product?.offerType || "").toLowerCase();
  return value === "bogo" || value === "mix_match" ? value : "";
};

export const getOfferLabel = (
  product?: { offerType?: string; offerText?: string } | null,
  fallbacks?: { bogo?: string; mixMatch?: string },
) => {
  const type = getOfferType(product);
  if (!type) return "";
  const text = String(product?.offerText || "").trim();
  if (text) return readableOfferText(text);
  if (type === "bogo") return fallbacks?.bogo || "BOGO";
  return fallbacks?.mixMatch || "Mix & Match";
};

// "Buy 1 Get 1 50% Off" reads as "150% off" once the digits sit side by side, so
// spell the discounted unit out: "Buy 1, Get 1 at 50% Off".
const readableOfferText = (text: string) =>
  text.replace(/^Buy\s+(\d+)\s+Get\s+(\d+)\s+([\d.]+%\s+Off)$/i, "Buy $1, Get $2 at $3");

export type MultiBuyOffer = {
  kind: "n_for" | "bogo_free" | "bogo_pct";
  qty: number;
};

export const parseMultiBuyOffer = (text: string): MultiBuyOffer | null => {
  const raw = String(text || "").trim();
  const nFor = raw.match(/^(\d+)\s+for\s+\$?\s*(\d+(?:\.\d+)?)$/i);
  if (nFor) {
    const qty = Number(nFor[1]);
    if (qty > 1 && Number(nFor[2]) > 0) return { kind: "n_for", qty };
  }
  const free = raw.match(/^Buy\s+(\d+)\s+Get\s+(\d+)\s+Free$/i);
  if (free) {
    const buy = Number(free[1]);
    const get = Number(free[2]);
    if (buy > 0 && get > 0) return { kind: "bogo_free", qty: buy + get };
  }
  const pct = raw.match(/^Buy\s+(\d+),?\s+Get\s+(\d+)\s+(?:at\s+)?([\d.]+)%\s+Off$/i);
  if (pct) {
    const buy = Number(pct[1]);
    const get = Number(pct[2]);
    if (buy > 0 && get > 0) return { kind: "bogo_pct", qty: buy + get };
  }
  return null;
};

export type MultiBuyPriceDisplay = {
  headline: string;
  qty: number;
  unitInDeal: number;
  regularEach: number;
  percentOff: number;
};

export const getMultiBuyPriceDisplay = (
  product: { offerType?: string; offerText?: string } | null | undefined,
  unitPrice: number,
  msrp: number,
): MultiBuyPriceDisplay | null => {
  const headline = getOfferLabel(product);
  if (!headline) return null;
  return {
    headline,
    qty: parseMultiBuyOffer(headline)?.qty ?? 0,
    unitInDeal: unitPrice,
    regularEach: msrp,
    percentOff: getDiscountPercent(msrp, unitPrice),
  };
};

export const getDiscountPercent = (msrp: number, price: number) => {
  if (msrp <= 0 || price >= msrp) return 0;
  return Math.round(((msrp - price) / msrp) * 100);
};

export const getEffectiveDiscountPercent = (msrp: number, price: number, cashback = 0, cashbackMinQty = 1) => {
  const units = cashbackMinQty > 1 ? cashbackMinQty : 1;
  const safeCashback = cashback > 0 ? cashback / units : 0;
  const effectivePrice = Math.max(0, price - safeCashback);
  const base = msrp > 0 ? msrp : price;
  if (base <= 0 || effectivePrice >= base) return 0;
  return Math.round(((base - effectivePrice) / base) * 100);
};

export const getSavingsAmount = (msrp: number, price: number) => {
  return msrp > price ? msrp - price : 0;
};

export const formatStoreId = (storeId: string) =>
  storeId.replace("-PENNY-ITEM", "").replace("-CRAZY-DEALS", "").replace("-COUPON", "");

type ProductItemPropsSubset = {
  index: number;
  data: {
    products: ProductListItem[];
    storeCounts?: Record<string, number>;
    showHotDeals?: boolean;
    showPennyItems?: boolean;
    searchProductId?: string | null;
    isSearching?: boolean;
    searchResults?: StoreProductResult[];
    homeDepotSearchResults?: HomeDepotStoreProductResult[];
    searchError?: string | null;
    isSavedMode?: boolean;
    sortBy?: string;
  };
};

export const areProductItemPropsEqual = (
  prev: ProductItemPropsSubset,
  next: ProductItemPropsSubset
) => {
  const prevProduct = prev.data.products[prev.index];
  const nextProduct = next.data.products[next.index];

  if (prevProduct !== nextProduct) return false;
  if (!prevProduct || !nextProduct) return false;

  const productId = getProductId(nextProduct);

  if (prev.data.storeCounts?.[productId] !== next.data.storeCounts?.[productId]) {
    return false;
  }
  if (prev.data.showHotDeals !== next.data.showHotDeals) return false;
  if (prev.data.showPennyItems !== next.data.showPennyItems) return false;

  const isSearchTarget = next.data.searchProductId === productId;
  const wasSearchTarget = prev.data.searchProductId === productId;
  if (isSearchTarget || wasSearchTarget) {
    if (prev.data.isSearching !== next.data.isSearching) return false;
    if (prev.data.searchResults !== next.data.searchResults) return false;
    if (prev.data.homeDepotSearchResults !== next.data.homeDepotSearchResults) {
      return false;
    }
    if (prev.data.searchError !== next.data.searchError) return false;
  }

  if (prev.data.isSavedMode !== next.data.isSavedMode) return false;
  if (prev.data.sortBy !== next.data.sortBy) return false;

  return true;
};
